import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, Send, CheckCircle } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const ForgotPasswordPage = () => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1770586968010-615ece906f63"
          alt="Calm water with lily pads"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/20" />
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="flex items-center gap-2 mb-12">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-12 w-auto" />
            <span className="font-heading text-xl font-semibold">{t("app.name")}</span>
          </Link>

          {sent ? (
            <div data-testid="forgot-password-success">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <h1 className="font-heading text-2xl font-semibold">{t("auth.resetEmailSent")}</h1>
              </div>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {t("auth.resetEmailSentDesc")}
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                {t("auth.resetEmailNotReceived")}
              </p>
              <div className="flex flex-col gap-3">
                <Button variant="outline" onClick={() => setSent(false)} data-testid="resend-email-btn">
                  <Send className="w-4 h-4 mr-2" /> {t("auth.resendEmail")}
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t("auth.backToLogin")}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div data-testid="forgot-password-form">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2">
                {t("auth.forgotPassword")}
              </h1>
              <p className="text-muted-foreground mb-8">
                {t("auth.forgotPasswordDesc")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading || !email.trim()}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? t("common.loading") : t("auth.sendResetLink")}
                  <Send className="w-4 h-4" />
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-6">
                <Link to="/login" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="back-to-login-link">
                  <ArrowLeft className="w-3 h-3" /> {t("auth.backToLogin")}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
